import { chipLabel } from '../../lib/tagLabel'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import st from './Stock.module.css'
import o from '../../components/Overlay.module.css'
import sd from './SdPanel.module.css'
import { CloseButton, SdPanel } from './SdPanel'
import { apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { useLayer, useChanged } from '../../components/layers'
import { PendingButton } from '../../components/pending'
import { hasCapability, useAccess } from '../../lib/access'
import { CreatePalletsPanel } from './CreatePalletsPanel'
import { PalletDrawer } from './PalletDrawer'
import { NoteBubble } from './NoteBubble'
import { OrderModal } from './OrderModal'
import { PSTATE, digits, fmtStamp, plural } from './model'
import {
  CHANGE_HELP,
  CHANGE_TITLE,
  allocateErrors,
  capacityNote,
  changeDone,
  changeLimit,
  changeMax,
  drawerSub,
  drawerTitle,
  historyLines,
  ledgerFacts,
  lineChoiceLabel,
  shortBanner,
  sourceLabel,
  type ChangeKind,
  type ChangePreview,
  type OrderLineChoice,
  type StockDetail,
} from './stock'
import { openableRow } from '../../lib/openableRow'

// The source-group drawer (V4 Stock → source; handoff §7n.5): the ledger's
// eight figures, what made the group and what is reserved, palletised and
// recorded against it, and the actions on it. Every action is a POST the API
// checks again; the drawer only reloads what it returns. The reservations and
// pallets open their own records over it, so the stock list stays behind.

type Panel =
  | null
  | { kind: 'change'; change: ChangeKind }
  | { kind: 'allocate' }
  | { kind: 'create' }
  | { kind: 'order'; id: string }
  | { kind: 'pallet'; id: string }

const errText = (e: any) => e?.body?.error || e?.message || String(e)

export function StockDrawer({
  user,
  sourceId,
  onClose,
  onChanged,
}: {
  user: any
  sourceId: string
  onClose: () => void
  onChanged: (message?: string) => void
}) {
  const [d, setD] = useState<StockDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [panel, setPanel] = useState<Panel>(null)
  const [helpOn, toggleHelp] = useHelpPref(user, 'stock_group')
  const access = useAccess()
  const mayOrder = hasCapability(access, 'create_edit_orders')
  const mayManage = hasCapability(access, 'manage_stock_dispatch')

  const userRef = useRef(user)
  userRef.current = user
  const load = useCallback(async () => {
    try {
      setD(await apiJson<StockDetail>(userRef.current, `/api/stock/${sourceId}`))
      setError(null)
    } catch (e) {
      setError(`Failed to load stock group: ${errText(e)}`)
    }
  }, [sourceId])

  useEffect(() => {
    load()
  }, [load])

  const changed = (msg?: string) => {
    setPanel(null)
    load()
    onChanged(msg)
  }

  const drawerEl = useRef<HTMLDivElement>(null)
  const drawer = useLayer(true, onClose, { el: drawerEl, modal: true })

  const s = d?.stockSource
  const l = d?.ledger
  const live = (d?.pallets || []).filter((p) => !p.cancelled && !p.departed)
  const history = d ? historyLines(d) : []

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && drawer.requestClose()}>
      <div
        ref={drawerEl}
        tabIndex={-1}
        className={`${o.drawer} ${st.drawer}`}
        role="dialog"
        aria-label={s ? drawerTitle(s) : 'Stock group'}
      >
        <div className={styles.dHead}>
          <div className={st.titleCol}>
            <span className={styles.dTitle}>{s ? drawerTitle(s) : 'Stock group'}</span>
            {s && <span className={st.headSub}>{drawerSub(s)}</span>}
          </div>
          <div className={styles.dHeadRight}>
            <button type="button" className={pa.helpBtn} onClick={toggleHelp}>
              {helpOn ? 'Hide help' : 'What these figures mean'}
            </button>
            <CloseButton title="Close: the stock list stays behind" onClick={drawer.requestClose} />
          </div>
        </div>
        {s && l && (mayOrder || mayManage) && (
          <div className={st.headActions} data-testid="stock-actions">
            {l.available > 0 && mayOrder && (
              <button type="button" className={pa.ghostBtn} onClick={() => setPanel({ kind: 'allocate' })}>
                Allocate to order
              </button>
            )}
            {mayManage && (
              <>
                {l.to_palletise > 0 && (
                  <button type="button" className={pa.primaryBtn} onClick={() => setPanel({ kind: 'create' })}>
                    Create pallets
                  </button>
                )}
                <button type="button" className={pa.ghostBtn} onClick={() => setPanel({ kind: 'change', change: 'hold' })}>
                  Hold
                </button>
                {l.source_held > 0 && (
                  <button type="button" className={pa.ghostBtn} onClick={() => setPanel({ kind: 'change', change: 'release' })}>
                    Release hold
                  </button>
                )}
                <button type="button" className={styles.dangerBtn} onClick={() => setPanel({ kind: 'change', change: 'write-off' })}>
                  Write off
                </button>
              </>
            )}
          </div>
        )}
        <div className={styles.dBody}>
          {error && (
            <div className={styles.alert} role="alert">
              {error}
            </div>
          )}
          {helpOn && (
            <div className={styles.helpBox} data-testid="stock-group-help">
              <button type="button" className={styles.helpX} title="Hide help" aria-label="Hide help" onClick={toggleHelp}>
                ×
              </button>
              <div className={styles.helpItem}>
                <span>Why this group exists</span>
                <p>
                  Harvest date, site and block, contractor, variety, box type, capacity and lid at picking all match. Change any one and the receipts
                  form a separate group. An order can require a specific harvest date or lid.
                </p>
              </div>
              <div className={styles.helpItem}>
                <span>What the actions do</span>
                <p>
                  Allocate reserves boxes against an order without moving them. Create pallets earmarks boxes onto planned pallets. Hold and Write off
                  both show an impact preview before they commit, including which orders go short.
                </p>
              </div>
            </div>
          )}
          {s && l && d && (
            <>
              {l.shortfall > 0 && <div className={styles.alertStrong}>{shortBanner(l)}</div>}
              <div className={st.tiles}>
                {ledgerFacts(l).map(([k, v]) => (
                  <div key={k} className={st.tile}>
                    <span className={st.tileK}>{k}</span>
                    <span className={st.tileV}>{v}</span>
                  </div>
                ))}
              </div>
              <div className={st.capNote}>{capacityNote(s)}</div>
              <div className={st.cards}>
                <div className={st.card}>
                  <div className={st.cardHead}>Source receipts</div>
                  {d.receipts.length === 0 && <div className={st.cardEmpty}>No receipts recorded.</div>}
                  {d.receipts.map((r) => (
                    <div key={r.id} className={st.cardRow}>
                      <span className={styles.strong}>{chipLabel(r.item_code) || r.cycle_id.slice(0, 8)}</span>
                      <span className={`${st.grow} ${styles.muted}`}>
                        {[fmtStamp(r.at), r.by || 'Unknown', r.how].filter(Boolean).join(' · ')}
                      </span>
                      <span className={st.num}>{r.boxes}</span>
                    </div>
                  ))}
                </div>
                <div className={st.card}>
                  <div className={st.cardHead}>Reservations</div>
                  {d.allocations.length === 0 && <div className={st.cardEmpty}>No order reserves from this group.</div>}
                  {d.allocations.map((a) => (
                    <div
                      key={a.id}
                      className={`${st.cardRow} ${st.cardRowLink}`}
                      data-testid="stock-reservation"
                      {...openableRow(() => setPanel({ kind: 'order', id: a.order_id }))}
                    >
                      <span className={styles.orderRef}>{a.order_reference}</span>
                      <span className={`${st.grow} ${styles.muted}`}>
                        {a.customer_name || '—'} {a.provisional && <span className={st.prov}>Provisional: order not yet approved</span>}
                      </span>
                      <span className={st.num}>{a.reserved}</span>
                    </div>
                  ))}
                </div>
                <div className={st.card}>
                  <div className={st.cardHead}>Planned and confirmed pallets</div>
                  {live.length === 0 && <div className={st.cardEmpty}>Nothing palletised yet.</div>}
                  {live.map((p) => (
                    <div
                      key={p.id}
                      className={`${st.cardRow} ${st.cardRowLink}`}
                      data-testid="stock-pallet"
                      {...openableRow(() => setPanel({ kind: 'pallet', id: p.id }))}
                    >
                      <span className={styles.strong}>
                        {p.pallet_id}
                        {p.note && <NoteBubble notes={[{ text: p.note }]} />}
                      </span>
                      <span className={`${st.grow} ${styles.muted}`}>
                        {plural(p.confirmed_boxes ?? p.planned_boxes, 'box', 'boxes')} · {p.order_reference || 'No order'}{' '}
                        {p.hold_state && <span className={st.red}>On hold</span>}
                      </span>
                      <span className={styles.chip} style={{ background: PSTATE[p.preparation_state][1], color: PSTATE[p.preparation_state][2] }}>
                        {PSTATE[p.preparation_state][0]}
                      </span>
                    </div>
                  ))}
                </div>
                <div className={st.card}>
                  <div className={st.cardHead}>Movement history</div>
                  {history.length === 0 && <div className={st.cardEmpty}>Nothing recorded yet.</div>}
                  {history.map((h) => (
                    <div key={h.key} className={styles.hist}>
                      <span className={styles.histAt}>{h.when}</span>
                      <span>
                        {h.what} <span className={styles.sub}>· {h.by}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {d && panel?.kind === 'change' && (
        <ChangePanel user={user} d={d} kind={panel.change} onCancel={() => setPanel(null)} onDone={changed} />
      )}
      {d && panel?.kind === 'allocate' && <AllocatePanel user={user} d={d} onCancel={() => setPanel(null)} onDone={changed} />}
      {panel?.kind === 'create' && (
        <CreatePalletsPanel user={user} sourceId={sourceId} onClose={() => setPanel(null)} onCreated={(msg) => changed(msg)} />
      )}
      {panel?.kind === 'order' && (
        <OrderModal
          user={user}
          orderId={panel.id}
          onClose={() => setPanel(null)}
          onChanged={(msg) => {
            load()
            onChanged(msg)
          }}
        />
      )}
      {panel?.kind === 'pallet' && (
        <PalletDrawer
          user={user}
          palletId={panel.id}
          onClose={() => setPanel(null)}
          onChanged={(msg) => {
            load()
            onChanged(msg)
          }}
        />
      )}
      {drawer.confirm}
    </div>
  )
}

// Hold, Release hold and Write off (V4 holdOrWriteoff). The help is always
// shown, not a toggle: both actions are misread. The impact preview is the
// API's (POST /api/stock/preview), read as you type; the commit reads the
// ledger again under a lock, so what it records is what it did.
function ChangePanel({
  user,
  d,
  kind,
  onCancel,
  onDone,
}: {
  user: any
  d: StockDetail
  kind: ChangeKind
  onCancel: () => void
  onDone: (message: string) => void
}) {
  const [qty, setQty] = useState('')
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState<ChangePreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const n = parseInt(qty, 10) || 0
  const limit = changeLimit(kind, d.ledger)
  const shown = preview && preview.boxes === n ? preview : null
  const over = n > limit
  const ok = n > 0 && !over && reason.trim().length > 0

  const userRef = useRef(user)
  userRef.current = user
  useEffect(() => {
    if (!(n > 0)) {
      setPreview(null)
      return
    }
    let live = true
    const t = setTimeout(() => {
      apiJson<ChangePreview>(userRef.current, '/api/stock/preview', {
        method: 'POST',
        body: { kind, stock_source_id: d.stockSource.id, boxes: n },
      })
        .then((r) => live && setPreview(r))
        .catch((e) => live && setError(errText(e)))
    }, 250)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [kind, n, d.stockSource.id])

  const submit = async () => {
    setBusy(true)
    try {
      await apiJson(userRef.current, `/api/stock/${kind}`, {
        method: 'POST',
        body: { stock_source_id: d.stockSource.id, boxes: n, reason: reason.trim() },
      })
      onDone(changeDone(kind, n, d.stockSource))
    } catch (e) {
      setError(errText(e))
      setBusy(false)
    }
  }

  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ qty, reason })
  const panel = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, modal: true, busy })
  const title = CHANGE_TITLE[kind]

  return (
    <SdPanel
      title={title}
      onCancel={panel.requestClose}
      confirm={panel.confirm}
      panelEl={panelEl}
      busy={busy}
      foot={
        <PendingButton
          type="button"
          className={kind === 'release' ? o.primary : o.dangerBtn}
          busy={busy}
          disabled={!ok}
          pending="Recording…"
          onClick={submit}
        >
          {title}
        </PendingButton>
      }
    >
      <div className={sd.text}>
        {sourceLabel(d.stockSource)}
        <div className={styles.sub}>{changeMax(kind, d.ledger)}</div>
      </div>
      <p className={sd.help}>{CHANGE_HELP[kind]}</p>
      <div className={sd.grid}>
        <label className={o.field}>
          <span className={o.fieldLabel}>Boxes</span>
          <input className={`${o.input} ${sd.num}`} inputMode="numeric" aria-label="Boxes" value={qty} onChange={(e) => setQty(digits(e.target.value))} />
        </label>
        <label className={o.field}>
          <span className={o.fieldLabel}>Reason (required)</span>
          <input className={o.input} aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
      {over && shown?.error && <div className={st.errText}>{shown.error}</div>}
      {error && (
        <div className={styles.errList} role="alert">
          {error}
        </div>
      )}
      <div className={sd.inset} data-testid="impact-preview">
        <div className={st.impactTitle}>Impact preview</div>
        {over ? (
          <div className={styles.sub}>No impact to show: {plural(n, 'box', 'boxes')} is more than this change can take.</div>
        ) : shown ? (
          shown.lines.map((line) => <div key={line}>• {line}</div>)
        ) : (
          <div className={styles.sub}>Enter a number of boxes.</div>
        )}
      </div>
    </SdPanel>
  )
}

// Allocate to order (V4 allocateFromStock): the order lines of the same
// variety and box type with boxes still unreserved. There is no provisional
// switch: an order not yet approved can be reserved for, and says so.
function AllocatePanel({
  user,
  d,
  onCancel,
  onDone,
}: {
  user: any
  d: StockDetail
  onCancel: () => void
  onDone: (message: string) => void
}) {
  const [choices, setChoices] = useState<{ available: number; lines: OrderLineChoice[] } | null>(null)
  const [lineId, setLineId] = useState('')
  const [qty, setQty] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const userRef = useRef(user)
  userRef.current = user
  useEffect(() => {
    apiJson<{ available: number; lines: OrderLineChoice[] }>(userRef.current, `/api/stock/${d.stockSource.id}/order-lines`)
      .then(setChoices)
      .catch((e) => setError(errText(e)))
  }, [d.stockSource.id])

  const available = choices?.available ?? d.ledger.available
  const line = choices?.lines.find((x) => x.line_id === lineId)
  const n = parseInt(qty, 10) || 0
  const blocks = allocateErrors(qty, available, line)
  const ok = !!line && n > 0 && blocks.length === 0

  const submit = async () => {
    if (!line) return
    setBusy(true)
    try {
      await apiJson(userRef.current, `/api/orders/${line.order_id}/lines/${line.line_id}/allocate`, {
        method: 'POST',
        body: { stock_source_id: d.stockSource.id, boxes: n },
      })
      onDone('Reserved ' + plural(n, 'box', 'boxes') + ' for ' + line.order_reference)
    } catch (e: any) {
      setError(errText(e))
      setNotice(e?.body?.notice || null)
      setBusy(false)
    }
  }

  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ lineId, qty })
  const panel = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, modal: true, busy })

  return (
    <SdPanel
      title="Allocate to order"
      onCancel={panel.requestClose}
      confirm={panel.confirm}
      panelEl={panelEl}
      busy={busy}
      foot={
        <PendingButton type="button" className={o.primary} busy={busy} disabled={!ok} pending="Reserving…" onClick={submit}>
          Reserve
        </PendingButton>
      }
    >
      <p className={sd.text}>
        {sourceLabel(d.stockSource)} · {available} available
      </p>
      <select
        className={o.input}
        aria-label="Order line"
        value={lineId}
        disabled={!choices}
        onChange={(e) => {
          const x = choices?.lines.find((c) => c.line_id === e.target.value)
          setLineId(e.target.value)
          setQty(x ? String(x.suggested) : '')
        }}
      >
        <option value="">{choices && choices.lines.length === 0 ? 'No order needs this product' : 'Choose an order line'}</option>
        {(choices?.lines || []).map((c) => (
          <option key={c.line_id} value={c.line_id}>
            {lineChoiceLabel(c)}
          </option>
        ))}
      </select>
      <div className={st.qtyRow}>
        <input className={`${o.input} ${sd.num} ${sd.qty}`} inputMode="numeric" aria-label="Boxes to reserve" value={qty} onChange={(e) => setQty(digits(e.target.value))} />
        {line && <span className={styles.sub}>{line.unallocated} boxes short on this line</span>}
      </div>
      {blocks.map((b) => (
        <div key={b} className={st.errText}>
          {b}
        </div>
      ))}
      {error && (
        <div className={styles.errList} role="alert">
          {error}
          {notice && <div>{notice}</div>}
        </div>
      )}
    </SdPanel>
  )
}
